/*  iceberg-ui  */
import { Box, Flex, Icon } from '@auviknetworks/iceberg-ui';

/*  MUI  */
import { Grid, IconButton, TextField, Typography } from '@mui/material';

/*  Shared  */
import {
    BusinessOwnerPicker,
    CategoryPicker,
    LifecyclePicker,
    RiskPicker
} from 'application/Shared/Components/Pickers';
import { FontSizes } from 'application/Shared/Constants';
import { Notify } from 'application/Shared/Helpers';
import { debug } from 'application/Shared/Utils';
import { TenantApp } from 'application/Shared/types/tenantApp';

/* Hooks */
import { usePutTenantApplicationLink } from 'hooks/dataAccess/Tenant/ApplicationLink/tenantApplicationLink';

/*  React  */
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';

/*  Styles  */
const sxs = {
    grid: {
        setting: { paddingBottom: '12px' }
    }
};

export interface BusinessOwnerReturnValue {
    businessOwnerPlatformUserId: null;
    managed3rdParty: false;
    managedByPartner: false;
    noBusinessOwnerRequired: false;
}

/**
 * A component that provides inline editing functionality for various fields in a tenant application
 * @param currentValue - The current value of the field being edited
 * @param displayValue - The display value shown when not in edit mode
 * @param field - The field name being edited (businessOwner, categoryId, status, risk, etc.)
 * @param handleDataChange - Optional callback to transform the data before saving
 * @param header - The header text to display above the field
 * @param multiline - Whether the text field should allow multiple lines
 * @param reloadApplication - Callback to reload the application data after saving
 * @param tenantApp - The tenant application object being edited
 */
export const InlineEdit = ({
    canEdit,
    currentValue,
    displayValue,
    field,
    handleDataChange,
    header,
    multiline,
    reloadApplication,
    tenantApp
}: {
    canEdit: boolean;
    currentValue?: string | undefined;
    displayValue: React.ReactNode;
    field: string;
    handleDataChange?: (value: string) => BusinessOwnerReturnValue;
    header: string;
    multiline?: boolean;
    reloadApplication: () => void;
    tenantApp: TenantApp;
}) => {
    const { t } = useTranslation();
    const { clientId } = useParams();
    const { putTenantApplicationLink } = usePutTenantApplicationLink();

    const [newData, setNewData] = useState<
        string | BusinessOwnerReturnValue | undefined
    >(currentValue);
    const [editing, setEditing] = useState(false);

    if (field === 'businessOwner') {
        if (
            (newData as BusinessOwnerReturnValue)
                ?.businessOwnerPlatformUserId === 'notAssigned'
        ) {
            setNewData({
                ...(newData as BusinessOwnerReturnValue),
                businessOwnerPlatformUserId: null
            });
        }
    }

    const saveData = async () => {
        try {
            const fullParams =
                field === 'businessOwner'
                    ? {
                          ...(newData as BusinessOwnerReturnValue),
                          platformTenantId: clientId
                      }
                    : { [field]: newData, platformTenantId: clientId };
            await putTenantApplicationLink({
                id: tenantApp?.id,
                params: fullParams
            });
            Notify.s(t('Application.saveDetails.success'));
            reloadApplication();
        } catch (e) {
            const err = t('Application.saveDetails.fail');
            debug.e(err, e);
            Notify.e(err);
        }
        setEditing(false);
    };

    const pickerOptions = {
        multiple: false,
        onChange: (value: string) => {
            const updatedValue = handleDataChange
                ? handleDataChange(value)
                : value;
            setNewData(updatedValue);
        },
        size: 'sm',
        value: newData,
        variant: 'outlined'
    };
    let picker;
    switch (field) {
        case 'businessOwner':
            picker = (
                <BusinessOwnerPicker
                    {...pickerOptions}
                    unselectedOption={t(
                        'ManageApplicationAdmin.no_user_selected'
                    )}
                    unselectedValue="notAssigned"
                    value={tenantApp.businessOwnerPlatformUserId}
                    noBusinessOwnerRequired={tenantApp.noBusinessOwnerRequired}
                    managedByPartner={tenantApp.managedByPartner}
                    managed3rdParty={tenantApp.managed3rdParty}
                />
            );
            break;
        case 'categoryId':
            picker = (
                <CategoryPicker
                    unselectedOption={t(
                        'ManageApplicationAdmin.no_category_selected'
                    )}
                    unselectedValue={null}
                    {...pickerOptions}
                />
            );
            break;
        case 'status':
            picker = <LifecyclePicker {...pickerOptions} />;
            break;
        case 'risk':
            picker = <RiskPicker {...pickerOptions} />;
            break;
        default:
            break;
    }

    if (!editing) {
        return (
            <Grid item xs={12} style={sxs.grid.setting}>
                <Flex sx={{ alignItems: 'center' }}>
                    <Typography color="textSecondary" fontWeight={600}>
                        {header}
                    </Typography>
                    {canEdit && (
                        <IconButton
                            data-trackid={`admin_application_inlineEdit_${field}`}
                            edge="end"
                            size="small"
                            color="secondary"
                            disableFocusRipple
                            onClick={() => setEditing(true)}
                        >
                            <Icon src="Pencil" size={FontSizes[2]} />
                        </IconButton>
                    )}
                </Flex>
                <Box>{displayValue}</Box>
            </Grid>
        );
    }

    return (
        <Grid item xs={12} style={sxs.grid.setting}>
            <Box>
                <Typography
                    color="textSecondary"
                    fontWeight={600}
                    marginBottom={1}
                >
                    {header}
                </Typography>
                <Flex sx={{ flexWrap: 'wrap' }}>
                    {picker ? (
                        <Flex sx={{ minWidth: '80%' }}>{picker}</Flex>
                    ) : (
                        <TextField
                            onChange={(e) => setNewData(e.target.value)}
                            value={newData}
                            variant="outlined"
                            style={{ minWidth: '80%' }}
                            size="small"
                            {...(multiline ? { multiline, rows: 4 } : {})}
                        />
                    )}
                    <Flex sx={{ justifyContent: 'flex-start' }}>
                        <IconButton
                            data-trackid={`admin_application_inlineEdit_${field}_save`}
                            size="small"
                            onClick={() => saveData()}
                            sx={{ color: 'icon.success' }}
                        >
                            <Icon src="CheckCircle" size={FontSizes[4]} />
                        </IconButton>
                        <IconButton
                            data-trackid={`admin_application_inlineEdit_${field}_cancel`}
                            size="small"
                            onClick={() => {
                                setEditing(false);
                                setNewData(currentValue);
                            }}
                            sx={{ color: 'icon.danger' }}
                        >
                            <Icon src="CloseCircle" size={FontSizes[4]} />
                        </IconButton>
                    </Flex>
                </Flex>
            </Box>
        </Grid>
    );
};
